import { ReactNode } from 'react'
import moment from 'moment'

import Link from 'components/atoms/Link'
import groups from 'constants/orderResultGroups'
import { PopoverWrapper } from 'components/atoms/PopoverWrapper'
import { TestCode } from 'store/types'
import { formatPhoneNumber } from 'helpers/format'
import { fullName } from 'helpers/nameHelper'
import { QUALIFICATION_GROUPS, uploadBadge } from './uploadBadge'

export const printIcon = (
  cell,
  row,
  idx: number,
  context: 'LAB' | 'DME',
  testType: TestCode,
  hideResults = false,
) => {
  // The order's own test type wins: a mixed listing may pass no (or a combined) test type.
  const badge =
    cell.name === 'UPLOAD' && !hideResults
      ? uploadBadge(row.progress?.UPLOAD, row.testType ?? testType)
      : null
  let icon
  if (cell.icon) {
    if (cell.icon_text) {
      icon = <small style={{ fontSize: '0.9rem' }}>{cell.icon}</small>
    } else if (badge) {
      icon = <small style={{ fontSize: '0.9rem' }}>{badge}</small>
    } else {
      icon = <i className={`fa fa-fw ${cell.icon}`}></i>
    }
  }

  const classMap = {
    PENDING: 'pending border-secondary',
    NOT_PROVIDED: 'pending border-secondary',
    PROVIDED: 'provided',
    PENDING_REVIEW: 'provided',
    ON_REVIEW: 'provided',
    PROMISED: 'incomplete border-white',
    WAITING: 'incomplete',
    FAILED: 'failed',
    WARNING: 'failed',
    DONE: 'verified',
    DONE_RX: 'verified',
    SKIPPED: 'secondary',
    G1: 'verified',
    G2: 'verified',
    G1A: 'verified',
    G3: 'dark',
  }
  const cellState =
    cell.name === 'ON'
      ? row.officeNotes === 'DONE_RX' || row.officeNotes === 'DONE'
        ? row.progress?.RX === 'DONE'
          ? 'DONE'
          : 'PROVIDED'
        : row.officeNotes === 'PENDING'
          ? 'PENDING'
          : row.officeNotes === 'PROMISED'
            ? 'PROMISED'
            : row.officeNotes
      : badge ?? row.progress?.[cell.name]
  // Qualification groups are results; show them all as a plain completed upload.
  const visibleState =
    hideResults && QUALIFICATION_GROUPS.includes(cellState) ? 'DONE' : cellState
  const contentClass = classMap[visibleState] ?? 'secondary'

  const Tooltip = renderTooltip(cell, row, context, testType, hideResults)
  const Component = cell.to ? Link : 'div'

  return (
    <Component
      className={`${!!contentClass ? '' : 'disabled'} btn btn-sm mr-1 ${
        contentClass == '' ? 'border border-secondary' : ''
      } btn-${contentClass}`}
      to={cell.to}>
      {Tooltip ? <Tooltip>{icon}</Tooltip> : icon}
    </Component>
  )
}

const tooltipProgressDesc = {
  PENDING: 'not yet uploaded or faxed in',
  PROVIDED: 'uploaded',
  PENDING_REVIEW: 'uploaded pending review',
  ON_REVIEW: 'uploaded and under review',
  PROMISED: 'awaiting receipt of fax',
  WAITING: 'not yet uploaded or faxed in',
  FAILED: 'failed lab verification',
  WARNING: 'failed lab verification',
  DONE: 'uploaded and approved',
}

const renderTooltip = (
  section: any,
  data: any,
  context: 'LAB' | 'DME',
  testType: TestCode,
  hideResults: boolean,
): null | (() => ReactNode) => {
  let tooltip: { customContent: JSX.Element; customHeader?: JSX.Element } | null = null
  if (section.name === 'COMMUNICATION') {
    tooltip = data?.faxing?.length && {
      customContent: (
        <div className="p-2">
          {data?.faxing?.map((fax) => (
            <p className="m-0 mb-1">
              {fax.status} {!hideResults && groups[fax.reportGroup]}:<br />{' '}
              {moment(fax.when).format('MM/DD/YYYY HH:mm a')}
            </p>
          ))}
        </div>
      ),
      customHeader: <>Fax Status</>,
    }
  } else if (section.name === 'UPLOAD') {
    const { progress } = data
    tooltip =
      data?.faxing?.length && testType !== 'HST' && !hideResults
        ? {
            customContent: (
              <div className="p-2">
                {data?.faxing?.map((fax) => (
                  <p className="m-0">
                    Report #{fax.reportId}:{' '}
                    {groups[fax.reportGroup] ? groups[fax.reportGroup] : 'unknown'}
                  </p>
                ))}
              </div>
            ),
            customHeader: <>Report Status</>,
          }
        : {
            customContent: (
              <div className="px-3 py-1">
                {progress.UPLOAD === 'PENDING' &&
                  (testType === 'HST' ? 'Report pending' : 'Device not yet uploaded')}
                {progress.UPLOAD === 'WAITING' &&
                  (testType === 'HST'
                    ? 'Report pending'
                    : 'Uploaded received pending session selection')}
                {progress.UPLOAD === 'G1' &&
                  !hideResults &&
                  (testType === 'HST' ? 'Report completed' : 'Device uploaded')}
                {hideResults &&
                  testType !== 'HST' &&
                  [...QUALIFICATION_GROUPS, 'DONE'].includes(progress.UPLOAD) &&
                  'Device uploaded'}
              </div>
            ),
          }
  } else if (section.name === 'PATIENT') {
    const { patient } = data || {}
    const address = patient?.address

    tooltip = patient && {
      customContent: (
        <div className="px-3 py-1">
          {address?.primaryAddressStreetLine1 ?? ''}
          <br />
          {address?.primaryAddressCity ?? ''}, {address?.primaryAddressState ?? ''}{' '}
          {address?.primaryAddressZip ?? ''} <br />
          {patient.homePhone ? formatPhoneNumber(patient.homePhone) : ''}
        </div>
      ),
      customHeader: <>Patient: {fullName(patient?.firstName, patient?.lastName)}</>,
    }
  } else if (section.name === 'PHYSICIAN') {
    const { physician } = data || {}
    tooltip = {
      customContent: (
        <div className="px-3 py-1">
          {physician?.lastName ? (
            <>
              <div>Dr. {fullName(physician.firstName, physician.lastName)}</div>
              {physician.npi && <div>NPI: {physician.npi}</div>}
              {physician.addressStreetLine1 && <div>{physician.addressStreetLine1}</div>}
              {physician.addressStreetLine2 && <div>{physician.addressStreetLine2}</div>}
              {(physician.addressCity || physician.addressState || physician.addressZip) && (
                <div>
                  {[physician.addressCity, physician.addressState, physician.addressZip]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
              {physician.fax && <div>Fax: {formatPhoneNumber(physician.fax)}</div>}
            </>
          ) : (
            'Physician not yet assigned to this order'
          )}
        </div>
      ),
    }
  } else if (section.name === 'PAYER') {
    const { payers } = data || {}
    tooltip = payers?.length
      ? {
          customContent: (
            <div className="px-3 py-1">
              {payers?.map((payer) => (
                <p className="m-0">
                  {payer.dmeName}{' '}
                  {context === 'LAB' && (
                    <>
                      <i className="fa fa-arrow-right" />{' '}
                      {payer.name ?? payer.names?.[0] ?? 'Not Assigned'}
                    </>
                  )}
                </p>
              ))}
            </div>
          ),
        }
      : null
  } else if (section.name === 'RX') {
    const { progress } = data || {}
    tooltip = {
      customContent: <div className="px-3 py-1">RX form {tooltipProgressDesc[progress.RX]}.</div>,
    }
  } else if (section.name === 'AOB' && testType !== 'HST') {
    const { progress } = data || {}
    tooltip = {
      customContent: <div className="px-3 py-1">AOB form {tooltipProgressDesc[progress.AOB]}.</div>,
    }
  } else if (section.name === 'ON') {
    const { officeNotes } = data || {}
    if (officeNotes === 'SKIPPED') {
      tooltip = {
        customContent: <div className="px-3 py-1">Office notes upload was skipped.</div>,
      }
    } else if (officeNotes && tooltipProgressDesc[officeNotes]) {
      tooltip = {
        customContent: (
          <div className="px-3 py-1">Office notes {tooltipProgressDesc[officeNotes]}.</div>
        ),
      }
    }
  }

  if (!tooltip) return null
  return ({ children }) => <PopoverWrapper {...tooltip}>{children}</PopoverWrapper>
}
